import React from "react";
import RichText from "./RichText";
import { PostChart } from "./PostCharts";

/**
 * Reader-side rendering of a post's blocks, in the order the author put them.
 *
 * Previously the page rendered the whole body and then every chart underneath it, so
 * a figure could never sit next to the sentence that referred to it — "as the chart
 * below shows" meant *four* charts below, and the reader had to work out which.
 *
 * A post written before blocks existed is served as one text block followed by its
 * charts (see `blocks_from_legacy` in backend/post_media.py), which is exactly how it
 * used to look. There is one rendering path, not one per generation of post.
 *
 * Everything authored — prose, chart captions, image captions — goes through
 * `RichText`, which is the only component allowed to render HTML. It is sanitised
 * server-side before it is stored; the client never decides what is safe.
 */
export default function PostBlocks({ blocks, className = "" }) {
  if (!blocks?.length) return null;
  return (
    <div className={className} data-testid="post-blocks">
      {blocks.map((b, i) => {
        if (b.type === "chart") {
          return <PostChart key={`c-${i}`} chart={b.chart} />;
        }
        if (b.type === "image") {
          return (
            <figure key={`i-${i}`} className="my-5" data-testid={`post-image-${i}`}>
              <img
                src={b.src}
                alt={b.alt || ""}
                width={b.width || undefined}
                height={b.height || undefined}
                // Explicit dimensions plus lazy loading: the layout does not jump as
                // images arrive, which on a long post is the difference between
                // reading and chasing the text down the page.
                loading="lazy"
                className="rounded-xl w-full h-auto"
              />
              {b.caption && (
                <figcaption className="mt-2">
                  <RichText html={b.caption} className="text-sm text-slate-500" />
                </figcaption>
              )}
            </figure>
          );
        }
        return (
          <RichText key={`t-${i}`} html={b.html}
                    className="text-[15px] leading-relaxed"
                    testid={i === 0 ? "post-body" : `post-text-${i}`} />
        );
      })}
    </div>
  );
}
